import { useAppSelector } from "../app/hooks";
import { ChatMessageType, type ChatMessageDto } from "../contracts";
import type { CommercialSimulatorAgentResponse } from "../models/agentResponseModels";
import type { ChatMessageViewModel } from "../models/chatMessageViewModel";
import { getToolRequestMessageById, isCommercialSimulatorSkuToolRequest, isCommercialSimulatorSubBrandToolRequest, isCommercialSimulatorToolRequest, type CommercialSimulatorSubBrandOptions, type CommercialSimulatorSubBrandToolRequest, type CommercialSimulatorToolRequest } from "../models/toolRequestModels";
import { getToolResponseById, getToolResponseMessageById, isCommercialSimulatorSubBrandToolResponse, isCommercialSimulatorToolResponse, type CommercialSimulatorSubBrandToolResponse, type CommercialSimulatorToolResponse, type ToolResponse } from "../models/toolResponseModels";
import { selectCurrentCommercialPlan, selectMessages } from "../slices/chatSlice";
import { capitalizeFirstLetter } from "../utils/stringValueConversion";
import { getUnitOfMeasureAbbreviation } from "../utils/unitOfMeasureUtils";

type CurrentPlanData = {
    scenarioDefaultName: string | null;
    chatConversationId: number | null;
    chatConversationMessageId: number | null;
    brandId: number | null;
    subBrandId: number | null;
    currentPlan: CommercialSimulatorAgentResponse | null;
    currentPlanData: SimulationPlanData | null;
    planStartDate: string;
    planEndDate: string;
    hasPlan: boolean;
    headingData: SimulationHeadingData | null;
    toolRequest: ChatMessageViewModel | null;
    toolResponse: ChatMessageViewModel | null;
};

export type SimulationHeadingData = {
    objective: string;
    skuName?: string;
    subBrandName?: string;
    retailers: string;
};

export type SimulationPlanData = {
    planningLevel: string;
    budget: string;
};

const emptyCurrentPlanData: CurrentPlanData = {
    scenarioDefaultName: null,
    chatConversationId: null,
    chatConversationMessageId: null,
    brandId: null,
    subBrandId: null,
    currentPlan: null,
    currentPlanData: null,
    planStartDate: "",
    planEndDate: "",
    hasPlan: false,
    headingData: null,
    toolRequest: null,
    toolResponse: null
};

type RetailerInput = {
    retailer_name?: string | null;
};

const getRetailerNames = (
    retailers: readonly RetailerInput[] | null | undefined
): string | null => {
    const retailerNames = retailers
        ?.filter(
            (retailer): retailer is RetailerInput & { retailer_name: string } =>
                Boolean(retailer.retailer_name)
        )
        .map(retailer => capitalizeFirstLetter(retailer.retailer_name))
        .join(", ");

    return retailerNames || null;
};

const getCommercialSimulationHeadingData = (
    chatMessages: ChatMessageDto[],
    toolRequest: CommercialSimulatorToolRequest
): SimulationHeadingData | null => {
    const input = toolRequest.input;

    if (!input?.objective || !input.product_name?.base_name) {
        return null;
    }

    const retailers = getRetailerNames(input.retailers);

    if (retailers == null) {
        return null;
    }

    const response = getToolResponseById<CommercialSimulatorToolResponse>(
        chatMessages,
        toolRequest.id
    );
    const result = {
        objective: capitalizeFirstLetter(input.objective),
        skuName: capitalizeFirstLetter(
            response?.tool_call_user_selection.sku_name ?? input.product_name?.base_name
        ),
        retailers
    };

    return result;
};

const getSizeInfo = (selection?: CommercialSimulatorSubBrandOptions | null): string => {
    if (selection?.unit == null
        || selection.unit_size == null
        || !Number.isFinite(selection.unit_size)
        || selection.unit_size <= 0) {
        return "";
    }

    const packSize = selection.units_per_pack && selection.units_per_pack > 1
        ? ` x ${selection.units_per_pack}`
        : "";

    return ` (${selection.unit_size}${getUnitOfMeasureAbbreviation(selection.unit)}${packSize})`;
};

const getSubBrandSimulationHeadingData = (
    chatMessages: ChatMessageDto[],
    toolRequest: CommercialSimulatorSubBrandToolRequest
): SimulationHeadingData | null => {
    const input = toolRequest.input;

    if (!input?.objective) {
        return null;
    }

    const retailers = getRetailerNames(input.retailers);

    if (retailers == null) {
        return null;
    }

    const response = getToolResponseById<CommercialSimulatorSubBrandToolResponse>(
        chatMessages,
        toolRequest.id
    );

    return {
        objective: capitalizeFirstLetter(input.objective),
        subBrandName: input.product_name
            ? `${input.product_name?.base_name}${getSizeInfo(response?.tool_call_user_selection)}`
            : "",
        retailers
    };
};

export const getSimulationHeadingData = (
    chatMessages: ChatMessageDto[] | undefined,
    payload: unknown
): SimulationHeadingData | null => {
    if (chatMessages == null) {
        return null;
    }

    if (isCommercialSimulatorSkuToolRequest(payload)) {
        return getCommercialSimulationHeadingData(chatMessages, payload);
    } else if (isCommercialSimulatorSubBrandToolRequest(payload)) {
        return getSubBrandSimulationHeadingData(chatMessages, payload);
    }

    return null;
};

export const getSimulationPlanData = (payload?: CommercialSimulatorSubBrandToolRequest | CommercialSimulatorToolRequest): SimulationPlanData | null => {
    if (!payload) {
        return null;
    }

    return {
        planningLevel: payload.plan_type.split("_").map(x => capitalizeFirstLetter(x)).join(" "),
        budget: payload.input?.plan_budget ?? ""
    };
};

const getScenarioDefaultName = (chatMessages: ChatMessageDto[] | undefined, toolResponse: ToolResponse, payload: unknown): string | null => {
    const count: number = chatMessages?.filter(message => message.type === ChatMessageType.AgentResponse).length ?? 0;

    if (isCommercialSimulatorToolRequest(payload) && isCommercialSimulatorToolResponse(toolResponse)) {
        const userSelection = toolResponse.tool_call_user_selection;
        return `${userSelection.sku_name} - ${userSelection.retailer_name} - ${payload.input?.objective} - ${payload.input?.plan_time_period_info} - v${count}`;
    }

    if (isCommercialSimulatorSubBrandToolRequest(payload) && isCommercialSimulatorSubBrandToolResponse(toolResponse)) {
        const userSelection = toolResponse.tool_call_user_selection;
        return `${userSelection.retailer_name} - ${payload.input?.objective} - ${payload.input?.plan_time_period_info} - v${count}`;
    }

    return null;
};

const useCurrentPlan = (): CurrentPlanData => {
    const chatMessages = useAppSelector(selectMessages);
    const currentPlan = useAppSelector(selectCurrentCommercialPlan);

    const toolRequestMessage = getToolRequestMessageById(chatMessages, currentPlan?.tool_use_id);
    const toolResponseMessage = getToolResponseMessageById(chatMessages, currentPlan?.tool_use_id);

    if (!currentPlan || !toolRequestMessage
        || !toolResponseMessage
        || (!isCommercialSimulatorSubBrandToolRequest(toolRequestMessage.payload)
            && !isCommercialSimulatorToolRequest(toolRequestMessage.payload))) {
        return emptyCurrentPlanData;
    }

    const planningData = getSimulationPlanData(toolRequestMessage.payload);
    const scenarioDefaultName = getScenarioDefaultName(chatMessages, toolResponseMessage.payload, toolRequestMessage.payload);
    const scenarioScope = isCommercialSimulatorToolResponse(toolResponseMessage.payload)
        || isCommercialSimulatorSubBrandToolResponse(toolResponseMessage.payload)
        ? toolResponseMessage.payload.tool_call_user_selection
        : null;

    const retailerPlan = currentPlan.plan_by_retailer[0];
    if (!retailerPlan) {
        throw new Error("Expected the current plan to contain at least one retailer plan.");
    }

    return {
        scenarioDefaultName,
        hasPlan: Boolean(currentPlan && toolRequestMessage && toolResponseMessage),
        chatConversationId: toolRequestMessage.message.chat_conversation_id ?? null,
        chatConversationMessageId: toolRequestMessage.message.id ?? null,
        brandId: scenarioScope?.brand_id ?? null,
        subBrandId: scenarioScope?.sub_brand_id ?? null,
        headingData: getSimulationHeadingData(
            chatMessages,
            toolRequestMessage.payload
        ),
        currentPlan: currentPlan ?? null,
        currentPlanData: planningData ?? null,
        planStartDate: retailerPlan?.retailer_plan_start_date ?? "",
        planEndDate: retailerPlan?.retailer_plan_end_date ?? "",
        toolRequest: toolRequestMessage.message ?? null,
        toolResponse: toolResponseMessage.message ?? null
    };
};

export default useCurrentPlan;
